import type { CreatePayRuleProfileInput } from "@crewledger/shared/schemas";
import { AlertTriangle, Plus } from "lucide-react";
import { EmptyState, FormHint } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { MICROCOPY } from "@/lib/microcopy";

export type PayRuleDraft = CreatePayRuleProfileInput["rules"][number];

export interface PayRuleBuilderProps {
  name: string;
  stackingMode: CreatePayRuleProfileInput["stackingMode"];
  rules: PayRuleDraft[];
  onNameChange: (name: string) => void;
  onStackingModeChange: (mode: CreatePayRuleProfileInput["stackingMode"]) => void;
  onRulesChange: (rules: PayRuleDraft[]) => void;
  onSubmit?: () => void;
  submitting?: boolean;
}

const RULE_TYPES: PayRuleDraft["type"][] = [
  "DAILY_OT",
  "DAILY_DT",
  "WEEKLY_OT",
  "WEEKEND",
  "HOLIDAY",
  "NIGHT_SHIFT",
  "HAZARD",
  "TRAINING",
];

const RULE_TYPE_LABELS: Record<PayRuleDraft["type"], string> = {
  DAILY_OT: "Daily overtime",
  DAILY_DT: "Daily double time",
  WEEKLY_OT: "Weekly overtime",
  WEEKEND: "Weekend premium",
  HOLIDAY: "Holiday premium",
  NIGHT_SHIFT: "Night shift",
  HAZARD: "Hazard pay",
  TRAINING: "Training rate",
};

const THRESHOLD_TYPES = new Set<PayRuleDraft["type"]>(["DAILY_OT", "DAILY_DT", "WEEKLY_OT"]);

function ruleNeedsThreshold(type: PayRuleDraft["type"]): boolean {
  return THRESHOLD_TYPES.has(type);
}

export function PayRuleBuilder({
  name,
  stackingMode,
  rules,
  onNameChange,
  onStackingModeChange,
  onRulesChange,
  onSubmit,
  submitting = false,
}: PayRuleBuilderProps) {
  const nameError = !name.trim();
  const rulesError = rules.some(
    (rule) =>
      !rule.multiplier ||
      (ruleNeedsThreshold(rule.type) && (rule.thresholdHours == null || rule.thresholdHours <= 0)),
  );

  const updateRule = (index: number, patch: Partial<PayRuleDraft>) => {
    onRulesChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  return (
    <div className="space-y-6 rounded-xl border-2 border-navy-blueprint/10 bg-card-white p-6 shadow-[var(--shadow-card)]">
      <div className="rounded-lg border border-navy-blueprint/15 bg-navy-deep/5 px-4 py-3 text-sm text-navy-deep">
        <p className="font-medium">Construction pay packages</p>
        <p className="mt-1 text-muted-foreground">
          Define how overtime, premiums, and multipliers apply across your crews.{" "}
          {MICROCOPY.auditLogSaved}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="profile-name">Profile name</Label>
          <Input
            id="profile-name"
            value={name}
            onChange={(e) => { onNameChange(e.target.value); }}
            placeholder="California OT Package"
            aria-invalid={nameError}
          />
          {nameError ? (
            <p className="text-xs text-red-danger">Profile name is required.</p>
          ) : (
            <FormHint>e.g. union package, prevailing wage, or site-specific rules</FormHint>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="stacking-mode">When multiple rules apply</Label>
          <select
            id="stacking-mode"
            value={stackingMode}
            onChange={(e) =>
              { onStackingModeChange(
                e.target.value as CreatePayRuleProfileInput["stackingMode"],
              ); }
            }
            className="h-10 w-full rounded-md border border-input px-3 text-sm"
          >
            <option value="HIGHEST_MULTIPLIER_WINS">Highest multiplier wins</option>
            <option value="STACK_MULTIPLIERS">Stack multipliers</option>
          </select>
          <FormHint>
            {stackingMode === "HIGHEST_MULTIPLIER_WINS"
              ? MICROCOPY.highestMultiplier
              : "Premiums multiply together — use only when your contract allows stacking."}
          </FormHint>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-navy-deep">Pay rules</h3>
            <FormHint>Rules run in order; stacking mode decides the final rate.</FormHint>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              { onRulesChange([
                ...rules,
                { type: "DAILY_OT", thresholdHours: 8, multiplier: 1.5, active: true },
              ]); }
            }
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title={BETA_EMPTY_STATES.payRules.title}
            description={BETA_EMPTY_STATES.payRules.description}
            actionLabel={BETA_EMPTY_STATES.payRules.actionLabel}
            onAction={() =>
              { onRulesChange([
                { type: "DAILY_OT", thresholdHours: 8, multiplier: 1.5, active: true },
              ]); }
            }
            className="py-8"
          />
        ) : (
          <ul className="space-y-3">
            {rules.map((rule, index) => (
              <li
                key={`${rule.type}-${index}`}
                className={cn(
                  "rounded-lg border p-4",
                  rule.active === false && "opacity-60",
                )}
              >
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                    <Label>Rule type</Label>
                    <select
                      value={rule.type}
                      onChange={(e) =>
                        { updateRule(index, { type: e.target.value as PayRuleDraft["type"] }); }
                      }
                      className="h-10 w-full rounded-md border border-input px-2 text-sm"
                    >
                      {RULE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {RULE_TYPE_LABELS[type]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {ruleNeedsThreshold(rule.type) ? (
                    <div className="space-y-1">
                      <Label>After (hours)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.5}
                        value={rule.thresholdHours ?? ""}
                        onChange={(e) =>
                          { updateRule(index, {
                            thresholdHours: e.target.value ? Number(e.target.value) : undefined,
                          }); }
                        }
                        placeholder="8"
                      />
                      <FormHint>Hours before this rate kicks in</FormHint>
                    </div>
                  ) : (
                    <div className="flex items-center rounded-md bg-bg-light px-3 py-2 text-xs text-muted-foreground">
                      No hour threshold — applies when flag is set on daily entry
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label>Multiplier</Label>
                    <Input
                      type="number"
                      min={1}
                      step={0.1}
                      value={rule.multiplier ?? ""}
                      onChange={(e) =>
                        { updateRule(index, {
                          multiplier: e.target.value ? Number(e.target.value) : undefined,
                        }); }
                      }
                      placeholder="1.5"
                    />
                    <FormHint>1.5 = time and a half</FormHint>
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={rule.active !== false}
                        onChange={(e) => { updateRule(index, { active: e.target.checked }); }}
                        className="rounded border-input"
                      />
                      Active
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { onRulesChange(rules.filter((_, i) => i !== index)); }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {onSubmit ? (
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          {rulesError ? (
            <p className="text-xs text-red-danger">
              Fix rule multipliers and hour thresholds before saving.
            </p>
          ) : (
            <FormHint>{MICROCOPY.auditLogSaved}</FormHint>
          )}
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitting || nameError || rulesError}
            className="sm:ml-auto"
          >
            {submitting ? "Saving…" : "Save pay profile"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
